import { useContext, useEffect, useState } from 'react'
import { Button, Col, Form, Row, Table, Modal, Badge } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import { getIncidents, deleteIncident, getIncidentAttachments, getIncidentAttachmentFile, getActiveIncidentCategories } from '../../../api/IncidentAPI'
import { format } from 'date-fns'
import { FaEye, FaTrash } from 'react-icons/fa'
import { RoleContextValue} from '../../types/OtherTypes'
import roleContext from '../../context/roleContext'
import { useAuth } from '../../modules/auth'
import ListPagination from '../../components/ListPagination'

interface IncidentListProps {
  onTabChange: (tab: string) => void
  refreshTrigger?: number
}

const IncidentList = ({ onTabChange, refreshTrigger }: IncidentListProps) => {
  const navigate = useNavigate()
  const { currentUser } = useAuth()

  const [incidents, setIncidents] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [incidentToDelete, setIncidentToDelete] = useState<string | null>(null)
  const [showAttachmentsModal, setShowAttachmentsModal] = useState(false)
  const [selectedIncidentAttachments, setSelectedIncidentAttachments] = useState<any[]>([])
  const [selectedIncidentId, setSelectedIncidentId] = useState<string | null>(null)
  const [loadingAttachments, setLoadingAttachments] = useState(false)
  const [showImageViewer, setShowImageViewer] = useState(false)
  const [selectedImage, setSelectedImage] = useState<string | null>(null)
  const [imageLoadError, setImageLoadError] = useState(false)
  const [categories, setCategories] = useState<any[]>([])
  
  const [filters, setFilters] = useState({
    status: '',
    categoryCode: '',
    priority: '',
    search: '',
  })
  const roleState: RoleContextValue = useContext(roleContext)
  const normalizedRoleName = (roleState.roleName || '').trim().replace(/[_-]+/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').replace(/\s+/g, ' ').toUpperCase()
  const isSuperAdmin = normalizedRoleName === 'SUPER ADMIN' || normalizedRoleName === 'SUPERADMIN'
  const isAdmin = normalizedRoleName === 'ADMIN'
  const isHrRole = normalizedRoleName === 'HR' || normalizedRoleName.startsWith('HR ')
  const canManage = isSuperAdmin || isAdmin || isHrRole || normalizedRoleName === 'POSH COMMITTEE MEMBER' || Boolean(currentUser?.isPoshCommitteeMember)
  const canAdminister = isSuperAdmin || isAdmin
  const canSeparateAssignedConcerns = !canAdminister && normalizedRoleName !== 'HR HEAD' && (
    isHrRole || normalizedRoleName === 'POSH COMMITTEE MEMBER' || Boolean(currentUser?.isPoshCommitteeMember)
  )
  const [concernView, setConcernView] = useState<'assigned' | 'mine'>('assigned')
  
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  })

  const fetchIncidents = async (filterValues = filters, page = pagination.page) => {
    setLoading(true)
    setError(null)
    try {
      const response = await getIncidents({
        ...filterValues,
        ...(canSeparateAssignedConcerns ? { view: concernView } : {}),
        page,
        limit: pagination.limit,
      })

      if (response.statusCode === 200) {
        setIncidents(response.data.incidents || [])
        setPagination(response.data.pagination || pagination)
      } else {
        setError(response.message || 'Failed to fetch incidents')
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch incidents')
    } finally {
      setLoading(false)
    }
  }

  const fetchCategories = async () => {
    try {
      const response = await getActiveIncidentCategories()
      if (response.statusCode === 200) {
        setCategories(response.data || [])
      }
    } catch (err: any) {
      console.error('Failed to fetch categories:', err)
    }
  }

  useEffect(() => {
    fetchIncidents()
    fetchCategories()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, pagination.limit, refreshTrigger, concernView])

  const handleFilterChange = (e: React.ChangeEvent<any>) => {
    const { name, value } = e.target
    setFilters(prev => ({ ...prev, [name]: value }))
  }

  const handleSearch = () => {
    setPagination(prev => ({ ...prev, page: 1 }))
    fetchIncidents(filters, 1)
  }

  const handleReset = () => {
    const resetFilters = {
      status: '',
      categoryCode: '',
      priority: '',
      search: '',
    }
    setFilters(resetFilters)
    setPagination(prev => ({ ...prev, page: 1 }))
    fetchIncidents(resetFilters, 1)
  }

  const handleViewDetail = (id: string) => {
    navigate(`/incident?tab=detail&id=${id}`)
  }

  const handleDelete = (id: string) => {
    setIncidentToDelete(id)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!incidentToDelete) return
    try {
      const response = await deleteIncident(incidentToDelete)
      if (response.statusCode === 202) {
        fetchIncidents()
      } else {
        alert(response.message || 'Failed to delete incident')
      }
    } catch (err: any) {
      alert(err.message || 'Failed to delete incident')
    } finally {
      setShowDeleteModal(false)
      setIncidentToDelete(null)
    }
  }

  const handleViewAttachments = async (incidentId: string) => {
    setSelectedIncidentId(incidentId)
    setShowAttachmentsModal(true)
    setLoadingAttachments(true)
    
    try {
      const response = await getIncidentAttachments(incidentId)
      if (response.statusCode === 200) {
        setSelectedIncidentAttachments(response.data || [])
      } else {
        alert(response.message || 'Failed to fetch attachments')
      }
    } catch (err: any) {
      alert(err.message || 'Failed to fetch attachments')
    } finally {
      setLoadingAttachments(false)
    }
  }

  const getFileIcon = (mimeType: string) => {
    if (mimeType?.startsWith('image/')) return '🖼️'
    if (mimeType === 'application/pdf') return '📄'
    if (mimeType?.includes('word')) return '📝'
    if (mimeType?.includes('excel') || mimeType?.includes('spreadsheet')) return '📊'
    return '📎'
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
  }

  const handleOpenAttachment = async (attachment: any) => {
    try {
      const blob = await getIncidentAttachmentFile(attachment.id, !attachment.mimeType?.startsWith('image/'))
      const objectUrl = URL.createObjectURL(blob)
      if (attachment.mimeType?.startsWith('image/')) {
        setImageLoadError(false)
        setSelectedImage(objectUrl)
        setShowImageViewer(true)
      } else {
        const link = document.createElement('a')
        link.href = objectUrl
        link.download = attachment.fileName
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000)
      }
    } catch (openError: any) {
      setError(openError.message || 'Unable to retrieve attachment.')
    }
  }

  const getStatusBadge = (status: string) => {
    const statusColors: Record<string, string> = {
      SUBMITTED: 'bg-primary-subtle text-primary',
      ACKNOWLEDGED: 'bg-info-subtle text-info',
      UNDER_REVIEW: 'bg-warning-subtle text-warning',
      PENDING_INFO: 'bg-light text-dark',
      ESCALATED_ICC: 'bg-danger-subtle text-danger',
      RESOLVED: 'bg-success-subtle text-success',
      CLOSED: 'bg-danger-subtle text-danger',
    }
    return statusColors[status] || 'bg-secondary'
  }

  const getPriorityBadge = (priority: string) => {
    const priorityColors: Record<string, string> = {
      HIGH: 'bg-danger',
      MEDIUM: 'bg-warning',
      LOW: 'bg-success',
    }
    return priorityColors[priority] || 'bg-secondary'
  }

  const getCategoryLabel = (categoryCode: string) => {
    const category = categories.find((cat) => cat.code === categoryCode)
    if (category) {
      return `${category.name} (${categoryCode})`
    }
    return categoryCode
  }

  const getRemainingDays = (slaDueDate: string | null) => {
    if (!slaDueDate) return '-'
    const dueDate = new Date(slaDueDate)
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    dueDate.setHours(0, 0, 0, 0)
    const diffTime = dueDate.getTime() - today.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    return diffDays
  }

  const getDaysOpenBadge = (createdAt: string) => {
    const daysOpen = Math.max(0, Math.floor((Date.now() - new Date(createdAt).getTime()) / 86400000))
    const color = daysOpen < 30 ? 'success' : daysOpen <= 60 ? 'warning' : 'danger'
    return <span className={`badge bg-${color}`}>{daysOpen}</span>
  }

  const getRemainingDaysBadge = (slaDueDate: string | null) => {
    const remainingDays = getRemainingDays(slaDueDate)
    if (remainingDays === '-') return <span className="text-muted">-</span>
    const days = remainingDays as number
    if (days < 0) {
      return <span className="badge rounded-pill px-3 py-2 bg-danger" style={{ fontSize: '0.75rem' }}>Overdue by {Math.abs(days)} days</span>
    } else if (days <= 3) {
      return <span className="badge rounded-pill px-3 py-2 bg-warning" style={{ fontSize: '0.75rem' }}>{days} days left</span>
    } else {
      return <span className="badge rounded-pill px-3 py-2 bg-success" style={{ fontSize: '0.75rem' }}>{days} days left</span>
    }
  }

  return (
    <div className="incident-list">
      <div className="card rounded-3 border-0 shadow-sm p-4 bg-body">
        <div className="mb-4">
          {canSeparateAssignedConcerns && (
            <div className="d-flex gap-2 mb-3" role="group" aria-label="Concern list">
              <Button
                variant={concernView === 'assigned' ? 'primary' : 'outline-secondary'}
                onClick={() => {
                  setConcernView('assigned')
                  setPagination(prev => ({ ...prev, page: 1 }))
                }}
              >
                Assigned to Me
              </Button>
              <Button
                variant={concernView === 'mine' ? 'primary' : 'outline-secondary'}
                onClick={() => {
                  setConcernView('mine')
                  setPagination(prev => ({ ...prev, page: 1 }))
                }}
              >
                Raised by Me
              </Button>
            </div>
          )}
          <Row className="g-3">
            <Col md={3}>
              <Form.Select
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
                className="border border-secondary-subtle rounded-2 py-2"
              >
                <option value="">All Status</option>
                <option value="SUBMITTED">Submitted</option>
                <option value="ACKNOWLEDGED">Acknowledged</option>
                <option value="UNDER_REVIEW">Under Review</option>
                <option value="PENDING_INFO">Pending Info</option>
                <option value="RESOLVED">Resolved</option>
                <option value="CLOSED">Closed</option>
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Select
                name="categoryCode"
                value={filters.categoryCode}
                onChange={handleFilterChange}
                className="border border-secondary-subtle rounded-2 py-2"
              >
                <option value="">All Categories</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.code}>
                    {category.name}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Select
                name="priority"
                value={filters.priority}
                onChange={handleFilterChange}
                className="border border-secondary-subtle rounded-2 py-2"
              >
                <option value="">All Priority</option>
                <option value="HIGH">High</option>
                <option value="MEDIUM">Medium</option>
                <option value="LOW">Low</option>
              </Form.Select>
            </Col>
            <Col md={3} className="d-flex gap-2 align-items-end">
              <Button
                onClick={handleSearch}
                className="rounded-2 py-2 px-4 border-0 btn-primary"
              >
                Search
              </Button>
              <Button
                onClick={handleReset}
                className="rounded-2 py-2 px-4 border border-secondary-subtle btn-outline-secondary"
              >
                Reset
              </Button>
            </Col>
          </Row>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : error ? (
          <div className="alert alert-danger border-0 rounded-3">{error}</div>
        ) : (
          <>
            <div className="table-responsive">
              <Table className="border-0 table-hover" hover>
                <thead>
                  <tr className="table-light">
                    <th className="py-3 px-4 fw-medium border-0" style={{ fontSize: '0.875rem' }}>Case ID</th>
                    <th className="py-3 px-4 fw-medium border-0" style={{ fontSize: '0.875rem' }}>Category Name</th>
                    {canManage ? <>
                    <th className="py-3 px-4 fw-medium border-0" style={{ fontSize: '0.875rem' }}>Category Code</th>
                    <th className="py-3 px-4 fw-medium border-0" style={{ fontSize: '0.875rem' }}>Description</th>
                    </> : <th className="py-3 px-4 fw-medium border-0" style={{ fontSize: '0.875rem' }}>Date Submitted</th>}
                    <th className="py-3 px-4 fw-medium border-0" style={{ fontSize: '0.875rem' }}>Status</th>
                    {canManage ? <>
                    <th className="py-3 px-4 fw-medium border-0" style={{ fontSize: '0.875rem' }}>Priority</th>
                    <th className="py-3 px-4 fw-medium border-0" style={{ fontSize: '0.875rem' }}>Reporter</th>
                    <th className="py-3 px-4 fw-medium border-0" style={{ fontSize: '0.875rem' }}>Assigned To</th>
                    <th className="py-3 px-4 fw-medium border-0" style={{ fontSize: '0.875rem' }}>Submitted On</th>
                    <th className="py-3 px-4 fw-medium border-0" style={{ fontSize: '0.875rem' }}>SLA Due Date</th>
                    <th className="py-3 px-4 fw-medium border-0" style={{ fontSize: '0.875rem' }}>Remaining Days</th>
                    <th className="py-3 px-4 fw-medium border-0" style={{ fontSize: '0.875rem' }}>Days Open</th>
                    <th className="py-3 px-4 fw-medium border-0" style={{ fontSize: '0.875rem' }}>Last Activity</th>
                    </> : <th className="py-3 px-4 fw-medium border-0" style={{ fontSize: '0.875rem' }}>Employee Communication</th>}
                    <th className="py-3 px-4 fw-medium border-0" style={{ fontSize: '0.875rem' }}>Attachments</th>
                    <th className="py-3 px-4 fw-medium border-0" style={{ fontSize: '0.875rem' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {incidents.length === 0 ? (
                    <tr>
                      <td colSpan={canManage ? 15 : 7} className="text-center py-5 text-muted">
                        No incidents found
                      </td>
                    </tr>
                  ) : (
                    incidents.map((incident) => (
                      <tr key={incident.id} className="border-bottom border-secondary-subtle" style={{ verticalAlign: 'middle' }}>
                        <td className="py-4 px-4">
                          <strong className="text-body">{incident.caseId}</strong>
                        </td>
                        <td className="py-4 px-4 text-body">{categories.find((cat) => cat.code === incident.categoryCode)?.name || '-'}</td>
                        {canManage ? <td className="py-4 px-4 text-body">{incident.categoryCode}</td> : null}
                        {!canManage && <td className="py-4 px-4 text-body">{incident.createdAt ? format(new Date(incident.createdAt), 'MMM dd, yyyy HH:mm') : '-'}</td>}
                        {canManage && (
                        <td className="py-4 px-4">
                          <div
                            style={{
                              maxWidth: '200px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {incident.description}
                          </div>
                        </td>
                        )}
                        <td className="py-4 px-4">
                          <span className={`badge rounded-pill px-3 py-2 ${getStatusBadge(incident.status)}`} style={{ fontSize: '0.75rem' }}>
                            {incident.status.replace(/_/g, ' ')}
                          </span>
                        </td>
                        {canManage ? <>
                        <td className="py-4 px-4">
                          <span className={`badge rounded-pill px-3 py-2 ${getPriorityBadge(incident.priority)}`} style={{ fontSize: '0.75rem' }}>
                            {incident.priority}
                          </span>
                        </td>
                        <td className="py-4 px-4 text-body">
                          {incident.reporter
                            ? `${incident.reporter.firstName} ${incident.reporter.lastName}`
                            : 'Anonymous'}
                        </td>
                        <td className="py-4 px-4 text-body">
                          {incident.assignedTo
                            ? `${incident.assignedTo.firstName} ${incident.assignedTo.lastName}`
                            : 'Unassigned'}
                        </td>
                        <td className="py-4 px-4 text-body">
                          {incident.createdAt
                            ? format(new Date(incident.createdAt), 'MMM dd, yyyy HH:mm')
                            : '-'}
                        </td>
                        <td className="py-4 px-4 text-body">
                          {incident.slaDueDate
                            ? format(new Date(incident.slaDueDate), 'MMM dd, yyyy')
                            : '-'}
                        </td>
                        <td className="py-4 px-4">
                          {getRemainingDaysBadge(incident.slaDueDate)}
                        </td>
                        <td className="py-4 px-4">{getDaysOpenBadge(incident.createdAt)}</td>
                        <td className="py-4 px-4 text-body">
                          {incident.updatedAt ? format(new Date(incident.updatedAt), 'MMM dd, yyyy HH:mm') : '-'}
                        </td>
                        </> : <td className="py-4 px-4 text-body">{incident.employeeCommunication || '-'}</td>}
                        <td className="py-4 px-4">
                          {/* {incident._count?.attachments > 0 ? (
                            <Badge
                              bg="info"
                              className="cursor-pointer"
                              style={{ cursor: 'pointer' }}
                              onClick={() => handleViewAttachments(incident.id)}
                            >
                              📎 {incident._count.attachments}
                            </Badge>
                          ) : (
                            <span className="text-muted">-</span>
                          )} */}
                          {incident._count?.attachments > 0 ? (
                            <Button
                              size="sm"
                              variant="outline-primary"
                              className="rounded-2 px-3"
                              onClick={() => handleViewAttachments(incident.id)}
                            >
                              View Attachment
                            </Button>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        {/* <td className="py-4 px-4">
                          <Button
                            size="sm"
                            onClick={() => handleViewDetail(incident.id)}
                            className="rounded-2 py-2 px-3 me-2 border-0 btn-primary"
                          >
                            View
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleDelete(incident.id)}
                            className="rounded-2 py-2 px-3 border border-secondary-subtle btn-outline-secondary"
                          >
                            Delete
                          </Button>
                        </td> */}
                        <td className="py-4 px-4">
                          <div className="d-flex align-items-center gap-2">
                            <Button
                              size="sm"
                              variant="primary"
                              className="d-flex align-items-center justify-content-center rounded-2"
                              style={{ width: '36px', height: '36px' }}
                              onClick={() => handleViewDetail(incident.id)}
                              title="View Incident"
                              aria-label="View Incident"
                            >
                              <FaEye />
                            </Button>

                            {canAdminister && (
                              <Button
                                size="sm"
                                variant="danger"
                                className="d-flex align-items-center justify-content-center rounded-2"
                                style={{ width: '36px', height: '36px' }}
                                onClick={() => handleDelete(incident.id)}
                                title="Delete Incident"
                                aria-label="Delete Incident"
                              >
                                <FaTrash />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>

            <ListPagination
              className="mt-4 pt-3 border-top border-secondary-subtle"
              page={pagination.page}
              pageSize={pagination.limit}
              totalRecords={pagination.total}
              disabled={loading}
              onPageChange={(page) => setPagination((previous) => ({ ...previous, page }))}
              onPageSizeChange={(limit) => setPagination((previous) => ({ ...previous, page: 1, limit }))}
            />
          </>
        )}
      </div>
      <Modal
          show={showDeleteModal}
          onHide={() => setShowDeleteModal(false)}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Delete Incident</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            Are you sure you want to delete this incident?
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => {
                setShowDeleteModal(false)
                setIncidentToDelete(null)
              }}
            >
              No
            </Button>
            <Button
              variant="danger"
              onClick={confirmDelete}
            >
              Yes
            </Button>
          </Modal.Footer>
        </Modal>

      <Modal
        show={showAttachmentsModal}
        onHide={() => {
          setShowAttachmentsModal(false)
          setSelectedIncidentAttachments([])
          setSelectedIncidentId(null)
        }}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Incident Attachments</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingAttachments ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : selectedIncidentAttachments.length === 0 ? (
            <div className="text-center py-5 text-muted">
              No attachments found for this incident
            </div>
          ) : (
            <div className="d-flex flex-column gap-3">
              {selectedIncidentAttachments.map((attachment) => (
                <div
                  key={attachment.id}
                  className="card border-0 shadow-sm p-3"
                >
                  <div className="d-flex align-items-center gap-3">
                    <div className="rounded-2 d-flex align-items-center justify-content-center bg-light" style={{ width: '48px', height: '48px', fontSize: '1.5rem' }}>
                      {getFileIcon(attachment.mimeType)}
                    </div>
                    <div className="flex-grow-1">
                      <h6 className="mb-1 fw-medium">{attachment.fileName}</h6>
                      <div className="text-muted" style={{ fontSize: '0.875rem' }}>
                        <span>{formatFileSize(attachment.fileSizeKb * 1024)}</span>
                        <span className="mx-2">•</span>
                        <span>
                          Uploaded by {attachment.uploadedBy?.firstName} {attachment.uploadedBy?.lastName}
                        </span>
                        <span className="mx-2">•</span>
                        <span>
                          {new Date(attachment.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    <Button variant="outline-primary" size="sm" onClick={() => handleOpenAttachment(attachment)}>
                      {attachment.mimeType?.startsWith('image/') ? 'Preview' : 'Download'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowAttachmentsModal(false)
              setSelectedIncidentAttachments([])
              setSelectedIncidentId(null)
            }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showImageViewer}
        onHide={() => {
          setShowImageViewer(false)
          setSelectedImage(null)
        }}
        centered
        size="xl"
        className="image-viewer-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Image Viewer</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {selectedImage ? (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
              {imageLoadError ? <div className="text-muted">This image could not be displayed.</div> : null}
              <img
                src={selectedImage}
                alt="Attachment"
                style={{
                  maxWidth: '100%',
                  maxHeight: '70vh',
                  objectFit: 'contain',
                }}
                onError={() => setImageLoadError(true)}
              />
            </div>
          ) : (
            <div className="text-center py-5 text-muted">
              No image to display
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowImageViewer(false)
              setSelectedImage(null)
            }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
    
  )
}

export default IncidentList
